import { isSiteRole, pageTitle, trialScreenOf, type Account, type Company, t } from '@procumi/shared';
import { useCallback, useEffect, useState } from 'react';
import { Rail } from './components/Rail';
import { DashboardPage } from './pages/DashboardPage';
import { Loading } from './components/StateBox';
import { clearAsyncCache } from './hooks/useAsync';
import { useToast } from './hooks/useToast';
import { api, errMsg, UNAUTHORIZED } from './lib/api';
import { AuthScreen } from './pages/AuthScreen';
import { InviteScreen } from './pages/InviteScreen';
import { MorningStopScreen } from './pages/MorningStopScreen';
import { ResetScreen } from './pages/ResetScreen';
import { ReportPage } from './pages/ReportPage';
import { SignupScreen } from './pages/SignupScreen';
import { OrderPage } from './pages/OrderPage';
import { AgentPage } from './pages/AgentPage';
import { ReorderPage } from './pages/ReorderPage';
import { BoqPage } from './pages/BoqPage';
import { NewRfqPage } from './pages/NewRfqPage';
import { RfqDetailPage } from './pages/RfqDetailPage';
import { RfqListPage } from './pages/RfqListPage';
import { OrdersPage } from './pages/OrdersPage';
import { SiteDetailPage } from './pages/SiteDetailPage';
import { SitesPage } from './pages/SitesPage';
import { PricesPage } from './pages/PricesPage';
import { AnalysisPage } from './pages/AnalysisPage';
import { HelpPage } from './pages/HelpPage';
import { SuppliersPage } from './pages/SuppliersPage';
import { CompanyPage } from './pages/CompanyPage';
import { NewRequisitionPage } from './pages/NewRequisitionPage';
import { PublicOfferPage } from './pages/PublicOfferPage';
import { LangSwitch } from './components/LangSwitch';
import { ReceivingPage } from './pages/ReceivingPage';
import { RequisitionPage } from './pages/RequisitionPage';
import { RequisitionsPage } from './pages/RequisitionsPage';
import type { Route } from './routes';
import { ask } from './lib/confirm';
import { setMarketCountry } from './lib/market';
import { setDomain } from './lib/terms';
import { reloadPending, syncLang } from './lib/i18n';
import { setCompany, setMe } from './lib/me';
import { mySignatureText } from './lib/signature';
import { PlanBanner } from './components/PlanBanner';
import { PlanFitGate } from './components/PlanFit';
import { CommandSearch } from './components/CommandSearch';
import { PlanDialog } from './components/PlanDialog';

/**
 * Codurile din linkurile de invitație, de parolă nouă și de înscriere, dacă
 * s-a intrat pe ele. Singurul loc în care aplicația citește adresa: restul navigării e ținut
 * în memorie.
 */
const codInvitatie = (): string => new URLSearchParams(window.location.search).get('invitatie') ?? '';
const codParola = (): string => new URLSearchParams(window.location.search).get('parola') ?? '';
const codInscriere = (): string => new URLSearchParams(window.location.search).get('inscriere') ?? '';
/** `?oferta=<token>`: furnizorul, din linkul primit pe mail. N-are cont și nu intră în aplicație. */
const codOferta = new URLSearchParams(window.location.search).get('oferta') ?? '';
/** `?dimineata-stop=<token>`: „Nu mai vreau acest mail”, din mailul de dimineață. Fără logare. */
const codDimineata = new URLSearchParams(window.location.search).get('dimineata-stop') ?? '';
/**
 * `?comanda=<cerere>:<comandă>`: linkul din mailul de aprobare. După intrare,
 * deschide direct comanda.
 */
function comandaDinLink(): Route | null {
  const params = new URLSearchParams(window.location.search);
  // `?raport`: linkul din raportul lunar pentru conducere
  if (params.has('raport')) return { view: 'report' };
  // `?agent`: linkul din notificările agentului
  if (params.has('agent')) return { view: 'agent' };
  // `?ajutor` / `?ajutor=<tichet>`: mailurile de la suport, cu răspunsul nostru
  if (params.has('ajutor')) return { view: 'help', ticket: params.get('ajutor') || undefined };
  // `?comanda-rapida`: comanda din nou la prețurile convenite
  if (params.has('comanda-rapida')) return { view: 'reorder' };
  // `?pas=…`: butoanele mailurilor din probă, fiecare la ecranul despre care vorbește (Ticket 08, P10)
  const pas = trialScreenOf(params.get('pas'));
  if (pas) return PAS_ROUTE[pas];
  // `?necesar=<id>`: linkul din mailurile necesarului de pe șantier
  const necesar = params.get('necesar');
  if (necesar) return { view: 'requisition', id: necesar };
  // `?cerere=<id>`: mailul „Ofertă nouă” venită prin link; `&followup=<trimitere>`:
  // mailul de dimineață, cu follow-up-ul deschis
  const cerere = params.get('cerere');
  if (cerere) return { view: 'rfq', id: cerere, followUp: params.get('followup') ?? undefined };
  // `?material=<cheie>`: un material anume din pagina Prețuri
  const material = params.get('material');
  if (material) return { view: 'prices', material };
  const [rfqId, orderId] = (params.get('comanda') ?? '').split(':');
  return rfqId && orderId ? { view: 'order', rfqId, orderId } : null;
}

/** Ecranul fiecărui `?pas=`: direct formularul, unde există unul. */
const PAS_ROUTE: Record<NonNullable<ReturnType<typeof trialScreenOf>>, Route> = {
  site: { view: 'sites', create: true },
  suppliers: { view: 'suppliers', import: true },
  new: { view: 'new' },
  rfqs: { view: 'rfqs', state: 'open' },
  plan: { view: 'company', tab: 'plan' },
};

/** Contul de șantier pornește din lista lui de necesar, nu din panou. */
const startFor = (a: Account): Route =>
  comandaDinLink() ?? (isSiteRole(a.user.role) ? { view: 'requisitions' } : { view: 'dashboard' });

/**
 * `?plata=ok|renuntat|portal`: întoarcerea de pe pagina Stripe. Pachetul îl pune
 * webhook-ul, care poate veni cu câteva secunde după om.
 */
const plata = new URLSearchParams(window.location.search).get('plata') ?? '';

/** `?cont-nou`: duce direct la înscriere (butonul „Încearcă gratuit” de pe site, din 2026-10-27). */
const contNou = (): boolean => new URLSearchParams(window.location.search).has('cont-nou');

export default function App() {
  if (codOferta) return <PublicOfferPage token={codOferta} />;
  if (codDimineata) return <MorningStopScreen token={codDimineata} />;
  return <Workspace />;
}

function Workspace() {
  // Ecranul de pornire e panoul: cum stă firma și ce ai de făcut azi.
  const [route, setRoute] = useState<Route>(() => comandaDinLink() ?? { view: 'dashboard' });
  const [invitatie, setInvitatie] = useState(codInvitatie);
  const [parola, setParola] = useState(codParola);
  const [inscriere, setInscriere] = useState(codInscriere);
  const [deschisa, setDeschisa] = useState(contNou);
  const [planDialog, setPlanDialog] = useState(false);
  const [account, setAccount] = useState<Account | null>(null);
  const [checking, setChecking] = useState(true);
  const { toast, toastEl } = useToast();

  // La pornire întrebăm cine suntem. 401 înseamnă doar „arată ecranul de intrare”.
  useEffect(() => {
    document.title = pageTitle();
    let alive = true;
    api
      .me()
      .then((a) => {
        if (!alive) return;
        setAccount(a);
        if (isSiteRole(a.user.role)) setRoute(startFor(a));
      })
      .catch(() => undefined)
      .finally(() => {
        if (alive) setChecking(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!plata) return;
    window.history.replaceState(null, '', window.location.pathname);
    if (plata === 'renuntat') {
      toast(t('Plata nu s-a făcut. Poți alege pachetul oricând din „Contul firmei”.'));
      return;
    }
    if (plata !== 'ok') return;
    toast(t('Mulțumim! Plata a trecut; pachetul apare în câteva secunde.'));
    // webhook-ul Stripe pune pachetul; citim firma din nou până apare
    const timers = [2_000, 6_000, 15_000].map((ms) =>
      window.setTimeout(() => {
        api
          .me()
          .then(setAccount)
          .catch(() => undefined);
      }, ms),
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, []);

  // Dacă sesiunea expiră în timpul folosirii, ne întoarcem la ecranul de intrare.
  useEffect(() => {
    const handler = () => {
      clearAsyncCache();
      setAccount(null);
    };
    window.addEventListener(UNAUTHORIZED, handler);
    return () => window.removeEventListener(UNAUTHORIZED, handler);
  }, []);

  const go = useCallback((r: Route) => {
    setRoute(r);
    window.scrollTo(0, 0);
  }, []);

  /** Scoate codul din bara de adrese, ca un refresh să nu-l mai ceară o dată. */
  const uitaInvitatia = useCallback(() => {
    setInvitatie('');
    window.history.replaceState(null, '', window.location.pathname);
  }, []);
  const uitaParola = useCallback(() => {
    setParola('');
    window.history.replaceState(null, '', window.location.pathname);
  }, []);
  const uitaInscrierea = useCallback(() => {
    setInscriere('');
    setDeschisa(false);
    window.history.replaceState(null, '', window.location.pathname);
  }, []);

  const signIn = (a: Account) => {
    clearAsyncCache();
    setAccount(a);
    setRoute(startFor(a));
  };

  // Linkul din mail (`?cerere=`, `?pas=`…) a ajuns în ecran: îl scoatem din adresă, ca un refresh să nu-l
  // mai deschidă. Nu cât timp prima intrare reîncarcă pagina pentru limba firmei: după reîncărcare,
  // aplicația pornește tot din adresă, deci linkul trebuie să fie încă acolo (Ticket 08, P10).
  useEffect(() => {
    if (account && !reloadPending() && comandaDinLink()) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [account]);

  const signOut = async () => {
    try {
      await api.logout();
    } catch {
      /* chiar dacă cererea eșuează, ieșim local */
    }
    clearAsyncCache();
    setAccount(null);
  };

  const loadDemo = async () => {
    const ok = await ask({
      title: t('Încarci datele demo?'),
      message: t('Se șterg furnizorii și cererile firmei, apoi se încarcă datele demo.'),
      confirm: t('Încarcă datele demo'),
      danger: true,
    });
    if (!ok) return;
    try {
      await api.resetDemo();
      clearAsyncCache();
      const fresh = await api.company();
      setAccount((a) => (a ? { ...a, company: fresh } : a));
      go({ view: 'dashboard' });
      toast(t('Datele demo au fost încărcate'));
    } catch (e) {
      toast(errMsg(e));
    }
  };

  if (checking) {
    return (
      <div className="auth">
        <Loading />
      </div>
    );
  }

  // Invitația bate sesiunea: dacă ai deschis linkul, ăsta e lucrul pe care ai
  // venit să-l faci, chiar dacă ești logat cu alt cont pe calculatorul ăla.
  if (invitatie) {
    return (
      <>
        <InviteScreen
          token={invitatie}
          onDone={(a) => {
            uitaInvitatia();
            signIn(a);
          }}
          onGiveUp={uitaInvitatia}
        />
        {toastEl}
        <LangSwitch />
      </>
    );
  }

  // Ca invitația: linkul de parolă nouă bate sesiunea.
  if (parola) {
    return (
      <>
        <ResetScreen
          token={parola}
          onDone={(a) => {
            uitaParola();
            signIn(a);
          }}
          onGiveUp={uitaParola}
        />
        {toastEl}
        <LangSwitch />
      </>
    );
  }

  // Linkul de înscriere: o firmă nouă, deci tot el bate sesiunea. Înscrierea
  // deschisă, doar fără sesiune: cine e deja în cont n-are de ce o vedea.
  if (inscriere || (deschisa && !account)) {
    return (
      <>
        <SignupScreen
          token={inscriere || null}
          onDone={(a) => {
            uitaInscrierea();
            signIn(a);
          }}
          onGiveUp={uitaInscrierea}
        />
        {toastEl}
        <LangSwitch />
      </>
    );
  }

  if (!account) {
    return (
      <>
        <AuthScreen onDone={signIn} onSignup={() => setDeschisa(true)} />
        {toastEl}
        <LangSwitch />
      </>
    );
  }

  syncLang(
    account.user.locale ?? 'ro',
    account.company.siteTerm,
    account.company.currency,
    account.company.country,
  );
  setMarketCountry(account.company.country);
  setDomain(account.company);
  setMe(account.user);
  setCompany(account.company);

  return (
    <>
      <div className="app">
        <Rail
          route={route}
          go={go}
          account={account}
          onLoadDemo={() => void loadDemo()}
          onSignOut={() => void signOut()}
          toast={toast}
          onUserChange={(patch) => setAccount({ ...account, user: { ...account.user, ...patch } })}
        />
        <main>
          <PlanBanner
            company={account.company}
            onChoose={account.user.isAdmin ? () => setPlanDialog(true) : undefined}
          />
          <PlanFitGate account={account} toast={toast} onChoosePlan={() => setPlanDialog(true)}>
            <Screen
              route={route}
              go={go}
              toast={toast}
              account={account}
              onCompanyChange={(company) => setAccount({ ...account, company })}
              onChoosePlan={() => setPlanDialog(true)}
            />
          </PlanFitGate>
        </main>
      </div>
      {planDialog ? (
        <PlanDialog
          company={account.company}
          toast={toast}
          onDone={(company) => setAccount({ ...account, company })}
          onClose={() => setPlanDialog(false)}
        />
      ) : null}
      {isSiteRole(account.user.role) ? null : <CommandSearch go={go} />}
      {toastEl}
    </>
  );
}

function Screen({
  route,
  go,
  toast,
  account,
  onCompanyChange,
  onChoosePlan,
}: {
  route: Route;
  go: (r: Route) => void;
  toast: (m: string) => void;
  account: Account;
  onCompanyChange: (company: Company) => void;
  onChoosePlan: () => void;
}) {
  // Contul de șantier vede necesarul și livrările; orice alt ecran duce la necesar.
  if (isSiteRole(account.user.role)) {
    if (route.view === 'requisition') return <RequisitionPage id={route.id} go={go} toast={toast} />;
    if (route.view === 'receiving') return <ReceivingPage toast={toast} />;
    if (route.view === 'newRequisition')
      return (
        <NewRequisitionPage
          key={route.editId ?? 'new'}
          go={go}
          toast={toast}
          siteId={route.siteId}
          editId={route.editId}
        />
      );
    return <RequisitionsPage go={go} toast={toast} />;
  }
  switch (route.view) {
    case 'requisitions':
      return (
        <RequisitionsPage key={route.group ?? 'auto'} go={go} toast={toast} initialGroup={route.group} />
      );
    case 'requisition':
      return <RequisitionPage key={route.id} id={route.id} go={go} toast={toast} />;
    case 'newRequisition':
      return (
        <NewRequisitionPage
          key={route.editId ?? 'new'}
          go={go}
          toast={toast}
          siteId={route.siteId}
          editId={route.editId}
        />
      );
    case 'dashboard':
      return (
        <DashboardPage
          company={account.company}
          onCompanyChange={onCompanyChange}
          go={go}
          toast={toast}
          signature={mySignatureText(account)}
          userName={account.user.name}
        />
      );
    case 'sites':
      return <SitesPage key={route.create ? 'create' : 'list'} go={go} toast={toast} create={route.create} />;
    case 'site':
      return <SiteDetailPage id={route.id} go={go} toast={toast} />;
    case 'rfqs':
      return <RfqListPage go={go} initialQ={route.q} initialState={route.state} />;
    case 'orders':
      return <OrdersPage key={route.group ?? 'all'} go={go} initialGroup={route.group} />;
    case 'rfq':
      return (
        <RfqDetailPage
          key={`${route.id}:${route.openOrder ? 1 : 0}:${route.followUp ?? ''}:${route.inbox ?? ''}`}
          id={route.id}
          openOrder={route.openOrder}
          followUp={route.followUp}
          inbox={route.inbox}
          go={go}
          toast={toast}
          signature={mySignatureText(account)}
          canExtract={account.features.offerExtraction}
        />
      );
    case 'order':
      return (
        <OrderPage
          rfqId={route.rfqId}
          orderId={route.orderId}
          go={go}
          toast={toast}
          logoUrl={api.logoUrl(account.company)}
        />
      );
    case 'new':
      return (
        <NewRfqPage
          key={route.fromBoq ? `boq:${route.fromBoq}` : (route.requisitionIds?.join() ?? 'new')}
          go={go}
          toast={toast}
          signature={mySignatureText(account)}
          siteId={route.siteId}
          requisitionIds={route.requisitionIds}
          fromBoq={route.fromBoq}
        />
      );
    case 'boq':
      return <BoqPage go={go} toast={toast} />;
    case 'reorder':
      return <ReorderPage key={route.tab ?? 'order'} go={go} toast={toast} tab={route.tab} />;
    case 'agent':
      return <AgentPage go={go} toast={toast} canExtract={account.features.offerExtraction} />;
    case 'suppliers':
      return <SuppliersPage key={route.import ? 'import' : 'list'} toast={toast} openImport={route.import} />;
    case 'prices':
      return <PricesPage key={route.material ?? ''} go={go} material={route.material} />;
    case 'analysis':
      return <AnalysisPage go={go} />;
    case 'report':
      return <ReportPage go={go} />;
    case 'help':
      return <HelpPage key={route.ticket ?? ''} go={go} ticket={route.ticket} />;
    case 'company':
      return (
        <CompanyPage
          key={route.tab ?? 'data'}
          initialTab={route.tab}
          isAdmin={account.user.isAdmin}
          company={account.company}
          toast={toast}
          onSaved={onCompanyChange}
          onChoosePlan={onChoosePlan}
        />
      );
  }
}
